import { AppThunkAction } from '../reducers';
import { ErrorType } from '../../models/HttpError';
import { ConceptSponsor } from '../../models/Entities/Contracts/ConceptSponsor';
import { NewConceptSponsor } from '../../models/Entities/Contracts/NewConceptSponsor';

// services
import * as Services from '../../services/ConceptSponsor.Services';

// actions
import * as Actions from '../actions';

type KnownAction =
  | Actions.ConceptSponsor.ConceptSponsorGetAllBySponsorIdRequestAction
  | Actions.ConceptSponsor.ConceptSponsorGetAllBySponsorIdReceiveAction
  | Actions.ConceptSponsor.ConceptSponsorGetAllBySponsorIdFailAction
  | Actions.ConceptSponsor.ConceptSponsorGetByIdRequestAction
  | Actions.ConceptSponsor.ConceptSponsorGetByIdReceiveAction
  | Actions.ConceptSponsor.ConceptSponsorGetByIdFailAction
  | Actions.ConceptSponsor.ConceptSponsorAddRequestAction
  | Actions.ConceptSponsor.ConceptSponsorAddReceiveAction
  | Actions.ConceptSponsor.ConceptSponsorAddFailAction
  | Actions.ConceptSponsor.ConceptSponsorUpdateRequestAction
  | Actions.ConceptSponsor.ConceptSponsorUpdateReceiveAction
  | Actions.ConceptSponsor.ConceptSponsorUpdateFailAction
  | Actions.ConceptSponsor.ConceptSponsorDeleteRequestAction
  | Actions.ConceptSponsor.ConceptSponsorDeleteReceiveAction
  | Actions.ConceptSponsor.ConceptSponsorDeleteFailAction;

const unauthorizedError = (): ErrorType => ({
  ErrorCode: 401,
  ErrorMessage: 'Not authorized',
  Errors: [],
});

const getToken = (getState: any): string | undefined => getState()?.oidc?.user?.access_token;

export const actionCreators = {
  GetAllConceptSponsors:
    (sponsorId: string): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const token = getToken(getState);
      if (token === undefined) {
        dispatch({
          type: 'CONCEPTSPONSOR_GET_ALL_BY_SPONSORID_FAIL_ACTION',
          sponsorId,
          error: unauthorizedError(),
        });
        return;
      }

      dispatch({
        type: 'CONCEPTSPONSOR_GET_ALL_BY_SPONSORID_REQUEST_ACTION',
        sponsorId,
      });

      Services.GetConceptSponsors(sponsorId, token)
        .then((conceptSponsors: ConceptSponsor[]) => {
          dispatch({
            type: 'CONCEPTSPONSOR_GET_ALL_BY_SPONSORID_RECEIVE_ACTION',
            sponsorId,
            conceptId: conceptSponsors[0]?.conceptId,
            conceptSponsors: conceptSponsors || [],
          });
        })
        .catch((error: ErrorType) =>
          dispatch({
            type: 'CONCEPTSPONSOR_GET_ALL_BY_SPONSORID_FAIL_ACTION',
            sponsorId,
            error,
          })
        );
    },

  GetConceptSponsorById:
    (conceptSponsorId: number): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const token = getToken(getState);
      if (token === undefined) {
        dispatch({
          type: 'CONCEPTSPONSOR_GET_BY_ID_FAIL_ACTION',
          conceptSponsorId,
          error: unauthorizedError(),
        });
        return;
      }

      dispatch({
        type: 'CONCEPTSPONSOR_GET_BY_ID_REQUEST_ACTION',
        conceptSponsorId,
      });

      Services.GetConceptSponsorById(conceptSponsorId, token)
        .then((conceptSponsor: ConceptSponsor) => {
          dispatch({
            type: 'CONCEPTSPONSOR_GET_BY_ID_RECEIVE_ACTION',
            conceptSponsor,
          });
        })
        .catch((error: ErrorType) =>
          dispatch({
            type: 'CONCEPTSPONSOR_GET_BY_ID_FAIL_ACTION',
            conceptSponsorId,
            error,
          })
        );
    },

  AddConceptSponsor:
    (newConceptSponsor: NewConceptSponsor): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const token = getToken(getState);
      if (token === undefined) {
        dispatch({
          type: 'CONCEPTSPONSOR_ADD_FAIL_ACTION',
          error: unauthorizedError(),
        });
        return;
      }

      dispatch({
        type: 'CONCEPTSPONSOR_ADD_REQUEST_ACTION',
        newConceptSponsor,
      });

      Services.CreateConceptSponsor(newConceptSponsor, token)
        .then((conceptSponsor: ConceptSponsor) => {
          dispatch({
            type: 'CONCEPTSPONSOR_ADD_RECEIVE_ACTION',
            conceptSponsor,
          });
        })
        .catch((error: ErrorType) =>
          dispatch({
            type: 'CONCEPTSPONSOR_ADD_FAIL_ACTION',
            error,
          })
        );
    },

  UpdateConceptSponsor:
    (conceptSponsor: ConceptSponsor): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const token = getToken(getState);
      if (token === undefined) {
        dispatch({
          type: 'CONCEPTSPONSOR_UPDATE_FAIL_ACTION',
          conceptSponsor,
          error: unauthorizedError(),
        });
        return;
      }

      dispatch({
        type: 'CONCEPTSPONSOR_UPDATE_REQUEST_ACTION',
        conceptSponsor,
      });

      Services.UpdateConceptSponsor(conceptSponsor, token)
        .then((updatedConceptSponsor: ConceptSponsor) => {
          dispatch({
            type: 'CONCEPTSPONSOR_UPDATE_RECEIVE_ACTION',
            conceptSponsor: updatedConceptSponsor,
          });
        })
        .catch((error: ErrorType) =>
          dispatch({
            type: 'CONCEPTSPONSOR_UPDATE_FAIL_ACTION',
            conceptSponsor,
            error,
          })
        );
    },

  DeleteConceptSponsor:
    (conceptSponsorId: number): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const token = getToken(getState);
      if (token === undefined) {
        dispatch({
          type: 'CONCEPTSPONSOR_DELETE_FAIL_ACTION',
          conceptSponsorId,
          error: unauthorizedError(),
        });
        return;
      }

      dispatch({
        type: 'CONCEPTSPONSOR_DELETE_REQUEST_ACTION',
        conceptSponsorId,
      });

      Services.DeleteConceptSponsor(conceptSponsorId, token)
        .then(() => {
          dispatch({
            type: 'CONCEPTSPONSOR_DELETE_RECEIVE_ACTION',
            conceptSponsorId,
          });
        })
        .catch((error: ErrorType) =>
          dispatch({
            type: 'CONCEPTSPONSOR_DELETE_FAIL_ACTION',
            conceptSponsorId,
            error,
          })
        );
    },
};
